import React, { useState, useMemo } from 'react';
import { countryPhoneCodes, CountryPhoneCode } from '../../../utils/CountryPhoneCodes';
import styles from './CountryCodeSelector.module.css';

interface CountryCodeSelectorProps {
  selectedCountryCode: string;
  onSelect: (country: CountryPhoneCode) => void;
  buttonClassName?: string;
}

const CountryCodeSelector: React.FC<CountryCodeSelectorProps> = ({
  selectedCountryCode,
  onSelect,
  buttonClassName = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Find the selected country
  const selectedCountry = useMemo(() => {
    return countryPhoneCodes.find(c => c.code === selectedCountryCode) || countryPhoneCodes[0];
  }, [selectedCountryCode]);

  // Filter countries based on search query
  const filteredCountries = useMemo(() => {
    if (!searchQuery.trim()) {
      return countryPhoneCodes;
    }
    const query = searchQuery.toLowerCase();
    return countryPhoneCodes.filter(country =>
      country.name.toLowerCase().includes(query) ||
      country.code.includes(query) ||
      country.flag.includes(query)
    );
  }, [searchQuery]);

  const handleSelect = (country: CountryPhoneCode) => {
    onSelect(country);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <>
      <button
        type="button"
        className={`${styles.countryCodeButton} ${buttonClassName}`}
        onClick={() => setIsOpen(true)}
        aria-label="Select country code"
      >
        <span className={styles.flag}>{selectedCountry.flag}</span>
        <span className={styles.code}>{selectedCountry.code}</span>
        <svg
          className={styles.chevron}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className={styles.modalOverlay} onClick={handleClose}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Select Country</h3>
              <button
                type="button"
                className={styles.closeButton}
                onClick={handleClose}
                aria-label="Close"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15 5L5 15M5 5L15 15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div className={styles.searchContainer}>
              <svg
                className={styles.searchIcon}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14 14L11.1 11.1"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search country..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>

            <div className={styles.countryList}>
              {filteredCountries.length === 0 ? (
                <div className={styles.noResults}>No countries found</div>
              ) : (
                filteredCountries.map((country, index) => (
                  <button
                    key={`${country.code}-${country.name}-${index}`}
                    type="button"
                    className={`${styles.countryItem} ${
                      selectedCountryCode === country.code ? styles.countryItemSelected : ''
                    }`}
                    onClick={() => handleSelect(country)}
                  >
                    <span className={styles.countryFlag}>{country.flag}</span>
                    <span className={styles.countryName}>{country.name}</span>
                    <span className={styles.countryCode}>{country.code}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CountryCodeSelector;

